import { defineStore } from 'pinia';
import { AxiosPromise } from 'axios';
import { Store } from '@/setup/globals';
import { ContactRegion } from '@/types/contact-region';
import { ContactPerson } from '@/types/contact-person';
import { ContactDepartment } from '@/types/contact-department';
import { ContactDivision } from '@/types/contact-division';
import { ObjectValues } from '@/types/object-values';
import normalizeContactPerson from '@/helpers/normalize-contact-person';

export const VISIBLE_FILTERS = {
  department: 'department',
  divisionCode: 'division',
  location: 'location',
} as const;

type VisibleFilters = ObjectValues<typeof VISIBLE_FILTERS>;

type State = {
  regions: ContactRegion[];
  visibleFilters: VisibleFilters[];
  divisions: ContactDivision[];
  departments: ContactDepartment[];
  contactPersons: ContactPerson[];
  showRestrictedFields: boolean;
  activeUrl?: string;
  postalCode?: string;
}

type ContactPersonFilters = {
  postalcode?: string;
  countrycode?: string;
  region?: string;
  division?: string;
  department?: string;
}

export type InitialData = {
  data: {
    postalCode: string;
    url: string;
    regions: ContactRegion[];
    visibleFilters: VisibleFilters[];
    contactPersons: ContactPerson[]; // Note: it seems like BE is ALWAYS delivering this initial data, even thought we don't show any contacts until a filter is applied (which triggers a request to the server)...
    departments: ContactDepartment[];
    divisions: ContactDivision[];
    showRestrictedFields: boolean;
  };
}

const storeName = Store.ContactList;

export default defineStore(storeName, {
  state: (): State => {
    const initialData = window.initialData?.[storeName];

    if (!initialData) {
      throw new Error(`Initial data for ${storeName} store missing`);
    }

    const {
      visibleFilters,
      regions,
      divisions,
      departments,
      showRestrictedFields,
      contactPersons,
      url,
      postalCode,
    } = initialData.data;

    return {
      visibleFilters,
      regions,
      divisions,
      departments,
      showRestrictedFields,
      contactPersons: contactPersons.map((person: ContactPerson) => normalizeContactPerson(person)),
      activeUrl: url,
      postalCode,
    };
  },
  getters: {
    getActiveRegionCode(): string | undefined {
      const search = new URLSearchParams(this.activeUrl?.split('?')[1]);

      return this.regions.find(region => region.selected)?.code || search.get('region') || undefined;
    },
  },
  actions: {
    apiGetFilteredContacts(filters: ContactPersonFilters): AxiosPromise {
      const url = new URL(this.$api.getApiUrl('contactPerson'), window.location.origin);

      Object.entries(filters)
        .forEach(([key, value]) => {
          if (value) {
            url.searchParams.set(key, value);
          }
        });

      return this.$api.get(url.toString())
        .then((response) => {
          const data = response.data?.data;

          if (!data) {
            return Promise.reject(new Error(`API failure during "${storeName}/apiGetFilteredContacts" request.`));
          }

          this.contactPersons = data.contactPersons.map((person: ContactPerson) => normalizeContactPerson(person));
          this.departments = data.departments;
          this.divisions = data.divisions;
          this.regions = data.regions;
          this.activeUrl = data.url;

          return response;
        });
    },
  },
});
